import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
/**
 * bundle-deals — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Bundle deals" section
 * (shrine-pro-ref/sections/bundle-deals.liquid, with the `.bundle-deals*` CSS ported VERBATIM from
 * assets/base.css). It is the "buy these N products together" band: a highlightable HEADING + optional
 * text over a `<bundle-deals>` widget that shows each bundled product's image (joined by a "+"), a
 * checkbox list of the products (title + discounted price + optional per-product variant pickers), a live
 * TOTAL, and a single "add selected to cart" button. Until every product block has a picked product it
 * shows the exact Shrine EMPTY state (placeholder images + a disabled "Select a product for every added
 * block" button).
 *
 * Field-type mapping (Shopify → ours, same convention as the newsletter / collage / comparison-table
 * rebuilds): inline_richtext/richtext → richtext (bold/italic → the `title-with-highlight` engine),
 * text → text, select → select, checkbox → checkbox, color → color, color_background → gradient,
 * image_picker → image, range → range, product → product-picker, header → group, visible_if →
 * visibleWhen. Ids are clean camelCase; labels / options / defaults stay faithful to Shrine (the 6 color
 * schemes + custom palette, the 13-pattern background, the 3 desktop layouts, the price-change / skip /
 * discount knobs, padding 36 / 36, and the `product` block with per-product discount + lock).
 *
 * BLOCK MODEL: Shrine ships `product` blocks (limit 5); we mirror that exactly as a LEGACY inline block
 * (`product` product-picker + per-block percentage / fixed discount + a "locked" checkbox), max 5. The
 * preset seeds 3 EMPTY product blocks — so a freshly-inserted section renders Shrine's placeholder empty
 * state until the seller picks products (identical to Shrine's own theme-editor behaviour).
 *
 * DELIBERATELY DEFERRED (faithful follow-ups, flagged — same staging as every storefront section):
 *  (a) a real add-to-cart POST — v1 renders the ATC button + the live client-side selection/total math,
 *      but the storefront cart/checkout endpoint is deferred across the whole storefront (COD demo);
 *  (b) PER-VARIANT price + availability — our StorefrontProduct model carries option LABELS (for the
 *      picker) but no per-variant price/stock, so changing a variant re-labels the pick without moving its
 *      price (Shopify recomputes from the variant); the markup/pickers are byte-faithful, the per-variant
 *      money is the deferred data layer;
 *  (c) `display_id` — Shopify dev "copy section id" utility, no storefront meaning (dropped, as elsewhere);
 *  (d) the compound `visible_if` on customMobileImageBackground (Shrine gates on custom AND image!=blank —
 *      our single-field VisibleWhen only expresses the `custom` half; the Component's mobile-image CSS
 *      no-ops without a desktop image, so it's inspector-only, same DSL limit as collage/newsletter).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const GENERAL = 'General';
const BUNDLES = 'Bundles';
const DISCOUNTS = 'Discounts';
const PADDING = 'Section padding';
export const bundleDealsSchema = {
    type: 'bundle-deals',
    name: 'Bundle deals',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── General: heading (+ both highlight engines) + size + text + color scheme + pattern ──────────
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: GENERAL,
            default: { format: 'html', html: '<p>Bundle deals</p>' },
            info: 'Bold (B) a word for highlight 1, italicise (I) a word for highlight 2.',
        },
        ...headingHighlightFields(1, GENERAL),
        ...headingHighlightFields(2, GENERAL),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: GENERAL,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: GENERAL,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: GENERAL,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: GENERAL,
        },
        // ── General: the 6 Shrine schemes + a full custom palette (same engine as newsletter/collage) ──
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: GENERAL,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customColorsBackground',
            type: 'color',
            label: 'Custom background color',
            group: GENERAL,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: GENERAL,
            info: 'If added, replaces the background color when applicable.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: GENERAL,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: GENERAL,
            info: 'If empty, the desktop image is also used on mobile.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsText',
            type: 'color',
            label: 'Custom text color',
            group: GENERAL,
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsAccent',
            type: 'color',
            label: 'Custom accents color',
            group: GENERAL,
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: GENERAL,
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonText',
            type: 'color',
            label: 'Custom solid button label',
            group: GENERAL,
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared 13-pattern engine) ─────────────────────────────
        ...patternFields('Background pattern'),
        // ── Bundles: layout + behaviour knobs ─────────────────────────────────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Desktop layout',
            group: BUNDLES,
            default: 'horizontal-images-left',
            options: [
                { value: 'vertical', label: 'Vertical' },
                { value: 'horizontal-images-left', label: 'Horizontal — Images left' },
                { value: 'horizontal-images-right', label: 'Horizontal — Images right' },
            ],
        },
        {
            id: 'enablePriceChanges',
            type: 'checkbox',
            label: 'Enable dynamic price changes',
            group: BUNDLES,
            default: true,
            info: 'Live item + total price updates after a variant/checkbox change.',
        },
        {
            id: 'skipUnavailable',
            type: 'checkbox',
            label: 'Hide & automatically skip sold out variants',
            group: BUNDLES,
            default: false,
        },
        {
            id: 'pricesUnder',
            type: 'checkbox',
            label: 'Display price under the title for products without a variant picker',
            group: BUNDLES,
            default: true,
        },
        {
            id: 'skipCart',
            type: 'checkbox',
            label: 'Skip cart',
            group: BUNDLES,
            default: false,
            info: 'Send customers straight to checkout after clicking add to cart.',
        },
        {
            id: 'totalPriceLabel',
            type: 'text',
            label: 'Total price label',
            group: BUNDLES,
            default: 'Total Price:',
            defaultKey: 'section.total_price',
        },
        {
            id: 'btnLabel',
            type: 'text',
            label: 'Add to cart button label',
            group: BUNDLES,
            default: 'Add selected to cart',
            defaultKey: 'section.add_selected',
        },
        // ── Discounts (section-level, applied on top of the per-product discounts) ─────
        {
            id: 'percentageDiscount',
            type: 'text',
            label: 'Percentage discount',
            group: DISCOUNTS,
            default: '0',
            info: 'Percentage discounted from the total price, WITHOUT the % symbol.',
        },
        {
            id: 'fixedAmountDiscount',
            type: 'text',
            label: 'Fixed amount discount',
            group: DISCOUNTS,
            default: '0',
            info: 'Fixed amount discounted from the total price, WITHOUT the currency symbol.',
        },
        mediaRadiusField,
        // ── Section padding (Shrine defaults 36 / 36) ─────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // The bundled PRODUCTS — Shrine `product` block, limit 5.
    blocks: [
        {
            type: 'product',
            name: 'Product',
            max: 5,
            fields: [
                { id: 'product', type: 'product-picker', label: 'Product' },
                {
                    id: 'percentageDiscount',
                    type: 'text',
                    label: 'Percentage discount',
                    default: '0',
                    info: 'Percentage discounted from this product, WITHOUT the % symbol.',
                },
                {
                    id: 'fixedAmountDiscount',
                    type: 'text',
                    label: 'Fixed amount discount',
                    default: '0',
                    info: 'Fixed amount discounted from this product, WITHOUT the currency symbol.',
                },
                {
                    id: 'required',
                    type: 'checkbox',
                    label: 'Checkbox locked',
                    default: false,
                    info: 'When enabled, prevents this product from being removed from the selection.',
                },
            ],
        },
    ],
    maxBlocks: 5,
};
export const bundleDealsPresets = [
    {
        name: 'Bundle deals',
        settings: {
            visibility: 'always-display',
            title: { format: 'html', html: '<p>Bundle deals</p>' },
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            headingSize: 'h1',
            colorScheme: 'background-1',
            layout: 'horizontal-images-left',
            enablePriceChanges: true,
            skipUnavailable: false,
            pricesUnder: true,
            skipCart: false,
            percentageDiscount: '0',
            fixedAmountDiscount: '0',
            patternBg: 'none',
            paddingTop: 36,
            paddingBottom: 36,
        },
        // 3 EMPTY product blocks (Shrine preset) → renders the placeholder empty state until products picked.
        blocks: [{ type: 'product' }, { type: 'product' }, { type: 'product' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const bundleDealsMeta = {
    label: 'Bundle deals',
    group: 'Content',
    icon: 'grid',
};
//# sourceMappingURL=schema.js.map